import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Card, MonoMeta, SummaryStripBase } from "@/design/primitives";
import { buildGoalsSummary, SUMMARY_SEPARATOR } from "@/lib/buildGoalsSummary";
import { GoalCard } from "./GoalCard";
import { GoalDetail } from "./GoalDetail";

// Render-layer freshness (council D1: never a silent frozen board). clock = boardClock().
const StalenessBanner = ({ clock }) => {
  if (clock.fresh) {
    return (
      <MonoMeta as="p" data-testid="goals-as-of" className="shrink-0 whitespace-nowrap pt-1 text-os-ink">
        as of {clock.stampText}
      </MonoMeta>
    );
  }
  return (
    <Card
      as="section"
      data-testid="goals-stale-banner"
      data-dead={clock.dead}
      aria-label="Board freshness warning"
      className="px-5 py-3"
    >
      <p className={cn("text-[0.875rem] leading-[1.45]", clock.dead ? "text-os-red" : "text-os-amber")}>
        {clock.dead
          ? "Board is not checking right now — Kit needs to look at the updater"
          : `Board info is from ${clock.stampText} — Kit's check-ins keep it current`}
      </p>
    </Card>
  );
};

// variant: "mobile" (single column, bottom sheet) | "desktop" (grid, side panel). clock: boardClock().
export const GoalsBoard = ({ cards, clock, variant = "desktop", initialOpenSlug = null, board = null }) => {
  const [openSlug, setOpenSlug] = useState(initialOpenSlug);
  const [showDone, setShowDone] = useState(false);
  const desktop = variant === "desktop";

  const summary = useMemo(() => buildGoalsSummary(cards), [cards]);
  const doneCount = cards.filter((c) => c.rawState === "ARCHIVED").length;
  const visible = useMemo(
    () => cards.filter((c) => showDone || c.rawState !== "ARCHIVED"),
    [cards, showDone],
  );
  const openItem = cards.find((c) => c.slug === openSlug);
  const close = useCallback(() => setOpenSlug(null), []);

  return (
    <div data-testid="goals-board" data-variant={variant} className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        <SummaryStripBase
          {...summary}
          separator={SUMMARY_SEPARATOR}
          size={desktop ? "lg" : "sm"}
          meta={<StalenessBanner clock={clock} />}
        />
        {!visible.length && (
          <Card quiet className="px-5 py-8 text-center">
            <p className="text-[0.9375rem] text-os-ink-2">No work on the board yet — chunks appear as Jeff asks for them</p>
          </Card>
        )}
        <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
          {visible.map((card) => (
            <GoalCard key={card.slug} card={card} onOpen={setOpenSlug} />
          ))}
        </div>
        {doneCount > 0 && (
          <button
            type="button"
            data-testid="goals-toggle-done"
            aria-expanded={showDone}
            onClick={() => setShowDone((s) => !s)}
            className={cn(
              "self-start font-mono text-[12px] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline",
              desktop ? "px-1 py-1" : "py-2",
            )}
          >
            {showDone ? "Hide finished work" : `Show finished work (${doneCount})`}
          </button>
        )}
      </div>
      {openItem && (
        <GoalDetail
          card={openItem}
          variant={variant}
          onClose={close}
          editor={board?.live ? { board, item: board.items.find((r) => r.slug === openSlug) } : null}
        />
      )}
    </div>
  );
};